import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Send, Mic, Loader2, X, Pencil, Check } from "lucide-react";
import type { ChatMessage } from "@/features/leads/api/useInternalChat";
import {
  applyMention,
  filterMentionUsers,
  findMentionToken,
  initialsOf,
  mentionRangeEndingAt,
  mentionSegments,
  mentionsPresentIn,
  type MentionRef,
  type MentionToken,
} from "@/features/chat/utils/mentionInput";
import { useAutoGrowTextarea } from "@/features/chat/utils/useAutoGrowTextarea";

interface ActionForChatProps {
  onSend: (text: string, mentions?: MentionRef[]) => void;
  onFileUpload: (file: File) => void;
  onVoiceNote: (blob: Blob) => void;
  isSending: boolean;
  replyTo?: ChatMessage | null;
  onCancelReply?: () => void;
  /** Message being edited — composer switches to "save" mode. */
  editing?: ChatMessage | null;
  onCancelEdit?: () => void;
  onSaveEdit?: (messageId: string, text: string, mentions?: MentionRef[]) => void;
  leadUsers?: { id: number; name: string; role: string }[];
}

/**
 * Internal-chat composer (lead drawer).
 *
 * WhatsApp keys: Enter sends, Shift+Enter breaks the line, Escape backs out
 * of the mention list / edit / reply. The box is a TEXTAREA — the old
 * `<input type="text">` threw away every line break in a paste, which is why
 * a copied log paragraph landed as one run-on line.
 */
export default function ActionForChat({
  onSend,
  onFileUpload,
  onVoiceNote,
  isSending,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  onSaveEdit,
  leadUsers = [],
}: ActionForChatProps) {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mentionToken, setMentionToken] = useState<MentionToken | null>(null);
  const [activeMention, setActiveMention] = useState(0);
  const [mentions, setMentions] = useState<MentionRef[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  // The highlight layer behind the textarea (chosen @Name tokens) and the
  // mention list's rows (kept in view while arrowing through it).
  const backdropRef = useRef<HTMLDivElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);

  useAutoGrowTextarea(inputRef, text, 6);

  // Entering edit mode loads the message into the box; leaving it (save,
  // cancel, channel switch, reply-instead) clears the draft.
  const editingId = editing?.id ?? null;
  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (editing) {
      wasEditingRef.current = true;
      setText(editing.body ?? "");
      setMentions((editing.mentions ?? []) as MentionRef[]);
      setMentionToken(null);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    } else if (wasEditingRef.current) {
      wasEditingRef.current = false;
      setText("");
      setMentions([]);
      setMentionToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // Restore the caret after a programmatic text change (mention insert).
  useEffect(() => {
    if (pendingCaretRef.current == null) return;
    const el = inputRef.current;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    if (el) {
      el.focus();
      el.setSelectionRange(pos, pos);
    }
  }, [text]);

  const syncMentionToken = (val: string, caret: number) => {
    const token = findMentionToken(val, caret);
    setMentionToken(token);
    setActiveMention(0);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    syncMentionToken(val, e.target.selectionStart ?? val.length);
  };

  const insertMention = (user: { id: number; name: string }) => {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? text.length;
    const token = mentionToken ?? findMentionToken(text, caret);
    if (!token) return;
    const next = applyMention(text, caret, token, user.name);
    pendingCaretRef.current = next.caret;
    setText(next.text);
    setMentions((prev) =>
      prev.some((m) => m.userId === user.id)
        ? prev
        : [...prev, { userId: user.id, name: user.name }]
    );
    setMentionToken(null);
  };

  const filteredUsers = mentionToken
    ? filterMentionUsers(leadUsers, mentionToken.query)
    : [];
  const mentionOpen = mentionToken !== null && filteredUsers.length > 0;

  // ↑/↓ moves the highlighted row; keep it visible, like WhatsApp's list.
  useEffect(() => {
    if (!mentionOpen) return;
    const row = mentionListRef.current?.querySelector<HTMLElement>(`[data-mention-index="${activeMention}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [activeMention, mentionOpen]);

  // The highlight layer scrolls with the box once it outgrows six lines.
  const syncBackdropScroll = () => {
    if (backdropRef.current && inputRef.current) backdropRef.current.scrollTop = inputRef.current.scrollTop;
  };
  useEffect(syncBackdropScroll, [text]);

  const reset = () => {
    setText("");
    setMentions([]);
    setMentionToken(null);
  };

  const handleSend = () => {
    const body = text.replace(/\r\n?/g, "\n").trim();
    if (!body || isSending) return;
    const finalMentions = mentionsPresentIn(body, mentions);
    if (editing) {
      if (body === (editing.body ?? "").trim()) {
        onCancelEdit?.();
        reset();
        return;
      }
      onSaveEdit?.(editing.id, body, finalMentions);
    } else {
      onSend(body, finalMentions.length > 0 ? finalMentions : undefined);
    }
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // WhatsApp: Backspace right after a chosen @Name removes the whole token.
    if (e.key === "Backspace" && !e.nativeEvent.isComposing) {
      const el = e.currentTarget;
      const caret = el.selectionStart ?? 0;
      if (caret === el.selectionEnd) {
        const range = mentionRangeEndingAt(text, caret, mentions);
        if (range) {
          e.preventDefault();
          const next = text.slice(0, range.start) + text.slice(range.end);
          pendingCaretRef.current = range.start;
          setText(next);
          setMentions((prev) => mentionsPresentIn(next, prev));
          setMentionToken(null);
          return;
        }
      }
    }
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveMention((i) => (i + 1) % filteredUsers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveMention(
          (i) => (i - 1 + filteredUsers.length) % filteredUsers.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredUsers[activeMention] ?? filteredUsers[0]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionToken(null);
        return;
      }
    }
    if (e.key === "Escape") {
      if (editing) {
        e.preventDefault();
        onCancelEdit?.();
        reset();
      } else if (replyTo) {
        e.preventDefault();
        onCancelReply?.();
      }
      return;
    }
    // Enter = send · Shift+Enter = new line (textarea default)
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    syncMentionToken(el.value, el.selectionStart ?? el.value.length);
  };

  const handleFileClick = () => fileInputRef.current?.click();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file);
      e.target.value = "";
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        setRecordingTime(0);
        if (chunksRef.current.length === 0) return;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        onVoiceNote(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000
      );
    } catch {
      /* mic denied */
    }
  }, [onVoiceNote]);

  const stopRecording = () => mediaRecorderRef.current?.stop();
  const cancelRecording = () => {
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current?.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setRecordingTime(0);
  };

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="relative">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.mp4,.doc,.docx,.webm,.mp3"
        onChange={handleFileChange}
      />

      {/* @mention dropdown — floats above the input */}
      {mentionOpen && (
        <div
          ref={mentionListRef}
          role="listbox"
          aria-label="Mention someone"
          className="absolute bottom-full left-0 right-0 mb-1 mx-4 bg-white rounded-xl shadow-lg border border-[#e6e8e7] max-h-[292px] overflow-y-auto z-20"
        >
          <div className="px-3 py-1.5 border-b border-[#f0f2f1]">
            <p className="text-[10px] font-medium text-[#9ba3a0] uppercase tracking-wider">
              Mention a user
            </p>
          </div>
          {filteredUsers.map((u, i) => (
            <button
              key={u.id}
              type="button"
              role="option"
              aria-selected={i === activeMention}
              data-mention-index={i}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertMention(u)}
              onMouseEnter={() => setActiveMention(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${
                i === activeMention ? "bg-[#eaf5f1]" : "hover:bg-[#f7f9f8]"
              }`}
            >
              <div className="size-7 rounded-full bg-[#eaf5f1] flex items-center justify-center">
                <span className="text-[9px] font-semibold text-[#218760]">
                  {initialsOf(u.name)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[13px] font-medium text-[#374741]">
                  {u.name}
                </span>
              </div>
              <span className="text-[10px] text-[#9ba3a0] bg-[#f0f2f1] px-1.5 py-0.5 rounded">
                {u.role}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Edit banner */}
      {editing && (
        <div className="px-4 py-2 flex items-center gap-2 border-b border-[#e6e8e7] bg-[#f7f9f8]">
          <Pencil size={14} className="text-[#218760] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-[#218760]">
              Editing message
            </p>
            <p className="text-[11px] text-[#697571] truncate">
              {editing.body}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onCancelEdit?.();
              reset();
            }}
            className="p-1 hover:bg-white rounded transition-colors"
            title="Cancel edit (Esc)"
          >
            <X size={14} className="text-[#9ba3a0]" />
          </button>
        </div>
      )}

      {/* Reply preview */}
      {!editing && replyTo && (
        <div className="px-4 py-2 flex items-center gap-2 border-b border-[#e6e8e7]">
          <div className="w-0.5 h-9 bg-[#218760] rounded-full shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-[#218760]">
              {replyTo.sender?.name}
            </p>
            <p className="text-[11px] text-[#697571] truncate">
              {replyTo.body || replyTo.type}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancelReply}
            className="p-1 hover:bg-[#f7f9f8] rounded transition-colors"
            title="Cancel reply (Esc)"
          >
            <X size={14} className="text-[#9ba3a0]" />
          </button>
        </div>
      )}

      {/* Input row */}
      <div className="flex gap-2 items-end px-4 py-3">
        {isRecording ? (
          <div className="flex-1 bg-red-50 border border-red-200 flex gap-3 h-11 items-center px-4 rounded-[20px]">
            <div className="size-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[13px] font-semibold text-red-600 tabular-nums w-12">
              {formatTime(recordingTime)}
            </span>
            <div className="flex-1 flex items-center gap-0.5">
              {Array.from({ length: 16 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-red-300"
                  style={{
                    height: `${4 + Math.random() * 12}px`,
                    animation: "pulse 0.8s ease-in-out infinite",
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
            <button
              onClick={cancelRecording}
              className="p-1 rounded-full hover:bg-red-100"
              title="Cancel"
            >
              <X size={16} className="text-red-400" />
            </button>
            <button
              onClick={stopRecording}
              className="p-1.5 rounded-full bg-red-500 hover:bg-red-600"
              title="Send"
            >
              <Send size={14} className="text-white" />
            </button>
          </div>
        ) : (
          <div className="flex-1 bg-white border border-[#cdd1d0] flex gap-3 min-h-11 items-end pl-4 pr-3 py-2 rounded-[20px]">
            {/* The box + a highlight layer behind it: chosen @Name tokens get
                a tinted background, like WhatsApp. The layer repeats the
                text (transparent) with the textarea's exact font and padding,
                so the tints sit under the words; the textarea's own text
                stays visible on top. */}
            <div className="relative flex-1 min-w-0 flex">
            <div
              ref={backdropRef}
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden text-[14px] font-normal tracking-[-0.56px] leading-[1.35] py-[3px] whitespace-pre-wrap break-words text-transparent"
            >
              {mentionSegments(text, mentions).map((seg, i) =>
                seg.mention ? (
                  <span key={i} className="rounded-[4px] bg-[#d6ebe3]">
                    {seg.text}
                  </span>
                ) : (
                  <span key={i}>{seg.text}</span>
                ),
              )}
            </div>
            <textarea
              ref={inputRef}
              rows={1}
              placeholder={
                editing
                  ? "Edit your message…"
                  : "Message... (@ to mention · Shift+Enter for a new line)"
              }
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onSelect={handleSelect}
              onClick={handleSelect}
              onScroll={syncBackdropScroll}
              disabled={isSending}
              className="relative flex-1 min-w-0 bg-transparent border-none outline-none resize-none text-[14px] font-normal text-[#505e59] tracking-[-0.56px] leading-[1.35] placeholder:text-[#505e59] py-[3px] whitespace-pre-wrap"
              style={{ scrollbarWidth: "thin" }}
            />
            </div>
            {!editing && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 p-0 hover:bg-transparent mb-[3px]"
                  onClick={handleFileClick}
                  disabled={isSending}
                  title="Attach a file — max 16MB"
                >
                  <Paperclip
                    size={18}
                    className="text-[#505e59]"
                    strokeWidth={1.5}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 p-0 hover:bg-transparent mb-[3px]"
                  onClick={startRecording}
                  disabled={isSending}
                >
                  <Mic size={18} className="text-[#505e59]" strokeWidth={1.5} />
                </Button>
              </>
            )}
          </div>
        )}

        {!isRecording && (
          <Button
            variant="primary"
            size="icon"
            className="size-11 rounded-[12px] border border-[#218760] shadow-[0px_1px_1px_0px_rgba(5,25,18,0.2)] shrink-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 27.887px -3.5px, rgba(70,165,128,1) 0%, rgba(60,136,107,1) 8.27%, rgba(50,107,85,1) 16.54%, rgba(40,77,64,1) 24.81%, rgba(30,48,42,1) 33.08%, rgba(40,77,64,1) 49.21%, rgba(50,107,85,1) 65.34%, rgba(60,136,107,1) 81.47%, rgba(70,165,128,1) 97.6%)",
            }}
            onClick={handleSend}
            disabled={isSending}
            title={editing ? "Save (Enter)" : "Send (Enter)"}
          >
            {isSending ? (
              <Loader2 size={20} className="text-white animate-spin" />
            ) : editing ? (
              <Check size={20} className="text-white" strokeWidth={2} />
            ) : (
              <Send size={20} className="text-white" strokeWidth={1.5} />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
